// OCT-7341: TreatyApp: File Activity panel — add search, filters, and a count of matching activities - Frontend : ashish.atwal@resolutionlife.com
// Pure helpers for the client-side searching/filtering of the Activity panel.
// Everything here operates on the events already loaded into the panel: it
// derives the person, folder and file "base name" from each event, builds the
// dropdown option lists, applies the combined filter + search, and supports
// highlighting matched text.
import { EventFragment, EventType } from '../../graphql/generated';
import { getEventDetails } from '../file-activity-panel/format-events';

// Filter state owned by the panel. Empty string / undefined means "no filter".
export type ActivityFilterState = {
  search: string;
  user: string;
  eventType?: EventType;
  folder: string;
  file: string;
};

export const emptyActivityFilterState: ActivityFilterState = {
  search: '',
  user: '',
  eventType: undefined,
  folder: '',
  file: '',
};

export const isActivityFilterActive = (filter: ActivityFilterState): boolean =>
  !!filter.search ||
  !!filter.user ||
  !!filter.eventType ||
  !!filter.folder ||
  !!filter.file;

// Service-account authors are surfaced as "Automation", matching the Event item.
const isServiceAccount = (event: EventFragment): boolean => {
  const email = event.user?.email;
  return !!email && email.split('@').includes('gserviceaccount.com');
};

export const getEventUser = (event: EventFragment): string =>
  isServiceAccount(event) ? 'Automation' : event.user?.name || 'Unknown user';

// The folder an event relates to, derived from its path (same rule as the Event item).
export const getEventFolder = (event: EventFragment, cedantName: string): string =>
  getEventDetails(cedantName, event.path).folder;

// The full file name for an event (empty for folder-only events like created folder).
export const getEventFilename = (event: EventFragment, cedantName: string): string =>
  getEventDetails(cedantName, event.path).filename;

/**
 * Derives the file "base name" used by the File filter: the trailing date stamp
 * (_YYYYMMDD or _YYYYMM) and the extension are stripped, so all dated versions of
 * a file group under one option. e.g. "AllianzLife_ID_Holdings_20260731.xlsx"
 * becomes "AllianzLife_ID_Holdings".
 */
export const getFileBaseName = (filename: string): string => {
  if (!filename) {
    return '';
  }
  // Drop the extension.
  let base = filename.replace(/\.[^.]+$/, '');
  // Drop a trailing _YYYYMMDD or _YYYYMM date segment.
  base = base.replace(/_\d{6,8}$/, '');
  return base;
};

type ActivityOptions = {
  users: string[];
  folders: string[];
  files: string[];
};

export type ActivityUserOption = { id: string; name: string };

export type ServerActivityOptions = {
  users: ActivityUserOption[];
  folders: string[];
  files: string[];
};

export const buildActivityFilterOptionsFromServer = (
  users: ActivityUserOption[],
  paths: string[],
  cedantName: string
): ServerActivityOptions => {
  const folders = new Set<string>();
  const files = new Set<string>();

  paths.forEach((path) => {
    const details = getEventDetails(cedantName, path);
    if (details.folder) {
      folders.add(details.folder);
    }
    const base = getFileBaseName(details.filename);
    if (base) {
      files.add(base);
    }
  });

  const sortAlpha = (a: string, b: string) => a.localeCompare(b);

  return {
    users: [...users].sort((a, b) => a.name.localeCompare(b.name)),
    folders: Array.from(folders).sort(sortAlpha),
    files: Array.from(files).sort(sortAlpha),
  };
};

/**
 * Builds the unique, sorted option lists for the User / Folder / File dropdowns
 * from the currently loaded events.
 */
export const buildActivityFilterOptions = (
  events: EventFragment[],
  cedantName: string
): ActivityOptions => {
  const users = new Set<string>();
  const folders = new Set<string>();
  const files = new Set<string>();

  events.forEach((event) => {
    const user = getEventUser(event);
    if (user) {
      users.add(user);
    }
    const folder = getEventFolder(event, cedantName);
    if (folder) {
      folders.add(folder);
    }
    const base = getFileBaseName(getEventFilename(event, cedantName));
    if (base) {
      files.add(base);
    }
  });

  const sortAlpha = (a: string, b: string) => a.localeCompare(b);

  return {
    users: Array.from(users).sort(sortAlpha),
    folders: Array.from(folders).sort(sortAlpha),
    files: Array.from(files).sort(sortAlpha),
  };
};

/**
 * Applies the combined filter + search to the loaded events. All active criteria
 * must match (AND). Search matches the person's name or the file name
 * (case-insensitive, plain text).
 */
export const applyActivityFilters = (
  events: EventFragment[],
  cedantName: string,
  filter: ActivityFilterState
): EventFragment[] => {
  const search = filter.search.trim().toLowerCase();

  return events.filter((event) => {
    const user = getEventUser(event);
    const filename = getEventFilename(event, cedantName);
    const folder = getEventFolder(event, cedantName);

    if (filter.user && user !== filter.user) {
      return false;
    }
    if (filter.eventType && event.type !== filter.eventType) {
      return false;
    }
    if (filter.folder && folder !== filter.folder) {
      return false;
    }
    if (filter.file && getFileBaseName(filename) !== filter.file) {
      return false;
    }
    if (search) {
      const inUser = user.toLowerCase().includes(search);
      const inFile = !!filename?.toLowerCase().includes(search);
      if (!inUser && !inFile) {
        return false;
      }
    }
    return true;
  });
};

export type SearchSuggestions = {
  users: string[];
  files: string[];
};

/**
 * Builds the type-ahead suggestions for the search box, grouped into Users and
 * File Names, from the loaded events matching the current term.
 */
export const buildSearchSuggestions = (
  events: EventFragment[],
  cedantName: string,
  term: string
): SearchSuggestions => {
  const lower = term.trim().toLowerCase();
  if (!lower) {
    return { users: [], files: [] };
  }

  const users = new Set<string>();
  const files = new Set<string>();

  events.forEach((event) => {
    const user = getEventUser(event);
    if (user?.toLowerCase().includes(lower)) {
      users.add(user);
    }
    const filename = getEventFilename(event, cedantName);
    if (filename?.toLowerCase().includes(lower)) {
      files.add(filename);
    }
  });

  return {
    users: Array.from(users).sort((a, b) => a.localeCompare(b)),
    files: Array.from(files).sort((a, b) => a.localeCompare(b)),
  };
};

/**
 * Splits a string into segments around a (case-insensitive, plain-text) search
 * term so callers can highlight the matched parts. Returns segments in order
 * with a `match` flag. Special regex characters in the term are escaped.
 */
export type HighlightSegment = { text: string; match: boolean };

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const splitForHighlight = (
  text: string,
  term: string
): HighlightSegment[] => {
  const trimmed = term.trim();
  if (!trimmed || !text) {
    return [{ text, match: false }];
  }
  const regex = new RegExp(`(${escapeRegExp(trimmed)})`, 'ig');
  return text
    .split(regex)
    .filter((segment) => segment !== '')
    .map((segment) => ({
      text: segment,
      match: segment.toLowerCase() === trimmed.toLowerCase(),
    }));
};
